import React, { useState, useRef, createElement } from 'react';
import { UploadIcon, XIcon, ImageIcon, PlusIcon } from 'lucide-react';
import { uploadFile, generateStoragePath } from '../firebase/storage';
interface MultiImageUploadProps {
  userId: string;
  onUploadsComplete: (downloadUrls: string[]) => void;
  onUploadError?: (error: Error) => void;
  maxImages?: number;
  initialImages?: string[];
  className?: string;
  maxWidth?: number;
  maxHeight?: number;
}
export const MultiImageUpload: React.FC<MultiImageUploadProps> = ({
  userId,
  onUploadsComplete,
  onUploadError,
  maxImages = 5,
  initialImages = [],
  className = '',
  maxWidth = 1200,
  maxHeight = 800
}) => {
  const [uploadingImages, setUploadingImages] = useState<{
    [key: string]: number;
  }>({});
  const [previewImages, setPreviewImages] = useState<string[]>(initialImages);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUploading = Object.keys(uploadingImages).length > 0;
  // Check if browser supports WebP
  const isWebPSupported = () => {
    const canvas = document.createElement('canvas');
    if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
      return true;
    }
    return false;
  };
  // Process image (resize and compress)
  const processImage = async (imageFile: File): Promise<File> => {
    return new Promise(resolve => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = height * maxWidth / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = width * maxHeight / height;
          height = maxHeight;
        }
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);
        // Convert to blob
        canvas.toBlob(blob => {
          if (blob) {
            // Create new file from blob
            const resizedFile = new File([blob], imageFile.name, {
              type: imageFile.type
            });
            resolve(resizedFile);
          } else {
            resolve(imageFile); // Fallback to original if conversion fails
          }
        }, imageFile.type, 0.8);
      };
      img.onerror = () => {
        resolve(imageFile); // Fallback to original if loading fails
      };
      // Load image
      const reader = new FileReader();
      reader.onload = e => {
        if (e.target?.result) {
          img.src = e.target.result as string;
        }
      };
      reader.onerror = () => resolve(imageFile);
      reader.readAsDataURL(imageFile);
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      // Check if adding these files would exceed the maximum
      if (previewImages.length + files.length > maxImages) {
        setError(`You can only upload a maximum of ${maxImages} images`);
        return;
      }
      // Reset error
      setError(null);
      // Upload each file
      files.forEach(async file => {
        // Only accept images
        if (!file.type.startsWith('image/')) {
          setError('Only image files are accepted');
          return;
        }
        // Check file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          setError('Image size should not exceed 5MB');
          return;
        }
        // Create a temporary ID for this upload
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImages(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
        // Track this upload
        setUploadingImages(prev => ({
          ...prev,
          [tempId]: 0
        }));
        try {
          // Process image before upload
          const processedFile = await processImage(file);
          // Start upload
          const path = generateStoragePath(userId, 'preview', processedFile.name);
          uploadFile(processedFile, path, progress => {
            setUploadingImages(prev => ({
              ...prev,
              [tempId]: progress
            }));
          }, error => {
            // Remove from tracking
            setUploadingImages(prev => {
              const newState = {
                ...prev
              };
              delete newState[tempId];
              return newState;
            });
            // Set error
            setError(`Upload failed: ${error.message}`);
            if (onUploadError) onUploadError(error);
          }, downloadUrl => {
            // Remove from tracking
            setUploadingImages(prev => {
              const newState = {
                ...prev
              };
              delete newState[tempId];
              return newState;
            });
            // Add to final URLs and call callback when all uploads are done
            const allUrls = [...previewImages.filter(url => url.startsWith('http')), downloadUrl];
            if (Object.keys(uploadingImages).length === 0) {
              onUploadsComplete(allUrls);
            }
          });
        } catch (err) {
          // Handle image processing error
          setUploadingImages(prev => {
            const newState = {
              ...prev
            };
            delete newState[tempId];
            return newState;
          });
          setError(`Image processing failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
          if (onUploadError) onUploadError(err instanceof Error ? err : new Error('Image processing failed'));
        }
      });
    }
  };
  const removeImage = (index: number) => {
    const newImages = [...previewImages];
    newImages.splice(index, 1);
    setPreviewImages(newImages);
    // Notify parent about the change
    const validUrls = newImages.filter(url => url.startsWith('http'));
    onUploadsComplete(validUrls);
  };
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  return <div className={`w-full ${className}`} data-id="element-1180">
      <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" multiple disabled={isUploading || previewImages.length >= maxImages} data-id="element-1181" />
      {error && <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-md" data-id="element-1182">
          {error}
        </div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4" data-id="element-1183">
        {/* Display existing images */}
        {previewImages.map((image, index) => <div key={index} className="relative aspect-video group" data-id="element-1184">
            <img src={image} alt={`Preview ${index + 1}`} className="w-full h-full object-cover rounded-lg border border-gray-200 dark:border-[#1A3330]" data-id="element-1185" />
            {!isUploading && <button type="button" onClick={() => removeImage(index)} className="absolute top-2 right-2 bg-black/50 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1186">
                <XIcon className="h-4 w-4" data-id="element-1187" />
              </button>}
          </div>)}
        {/* Add more button - only show if under max images */}
        {previewImages.length < maxImages && <button type="button" onClick={triggerFileInput} disabled={isUploading} className={`aspect-video flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-[#1A3330] hover:border-[#2DB489] dark:hover:border-[#4ED1A0] transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-1188">
            {isUploading ? <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#2DB489] dark:border-[#4ED1A0]" data-id="element-1189"></div> : <>
                <PlusIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A] mb-1" data-id="element-1190" />
                <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-1191">
                  Add Image
                </p>
                <p className="text-xs text-gray-400 dark:text-[#7A8F8A] mt-1" data-id="element-1192">
                  Images will be optimized
                </p>
              </>}
          </button>}
      </div>
      <p className="text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-1193">
        {previewImages.length} of {maxImages} images uploaded
      </p>
      {isUploading && <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md" data-id="element-1194">
          <p className="text-xs text-blue-600 dark:text-blue-400" data-id="element-1195">
            Optimizing and uploading images...
          </p>
        </div>}
    </div>;
};